import {
  Component,
  DestroyRef,
  Injector,
  NgZone,
  OnInit,
  QueryList,
  ViewChildren,
  afterNextRender,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormArray, FormGroup } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { DragDropRegistry } from '@angular/cdk/drag-drop';
import { Subject } from 'rxjs';
import { debounceTime, take, takeUntil } from 'rxjs/operators';
import {
  DraggableContainerComponent,
  DraggableItem,
  QaiButtonComponent,
  QaiFloatingDirective,
  QaiIconComponent,
  QaiTooltipComponent,
} from '@qai/ui';
import { TRANSLOCO_SCOPE, TranslocoModule } from '@jsverse/transloco';
import { AuthorReactiveFormService } from 'src/app/pages/self-serve/simulation-builder/services/author-reactive-form.service';
import { AuthorService } from 'src/app/services/self-serve/author.service';
import { IGuidedAnswerPointUpdate, IGuidedQuestionUpdate } from 'src/app/interfaces/self-serve/requests/iguided-questions-update';
import { IGuidedQuestion } from 'src/app/interfaces/self-serve/responses/iguided-questions-with-answers';
import {
  IKnowledgeCheckAnswerPoint,
  IKnowledgeCheckQuestionForm,
  KnowledgeCheckAnswerPointVerb,
} from 'src/app/interfaces/self-serve/isimulation-details-information';
import { SimulationConstants } from 'src/app/utils/constants/self-serve/simulation.constants';
import { KnowledgeCheckQuestionPanelComponent } from './knowledge-check-question-panel/knowledge-check-question-panel.component';

const QUESTION_FIELDS = ['title', 'script', 'hint', 'resourceNote'] as const;
const ANSWER_POINT_LIST_ID_PREFIX = 'answer-points-';
// How long an answer point is held over a collapsed question before the question expands.
const EXPAND_ON_DRAG_HOVER_DELAY = 400;

// List of Knowledge Check questions with the "Add new question" action.
// Questions live in the shared simulation form (top-level `questions`). Each question autosaves:
// it is created on its first content, then sent whole on every change (the update API writes every field as sent).
@Component({
  selector: 'knowledge-check-question-section-list',
  standalone: true,
  templateUrl: './knowledge-check-question-section-list.component.html',
  styleUrls: ['./knowledge-check-question-section-list.component.scss'],
  imports: [
    DraggableContainerComponent,
    QaiButtonComponent,
    QaiIconComponent,
    QaiTooltipComponent,
    QaiFloatingDirective,
    KnowledgeCheckQuestionPanelComponent,
    TranslocoModule,
  ],
  providers: [{ provide: TRANSLOCO_SCOPE, useValue: 'simulationBuilder' }],
})
export class KnowledgeCheckQuestionSectionListComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);

  questions?: FormArray<FormGroup>;
  questionItems: DraggableItem[] = [];
  hasBlankQuestion = signal(false);

  callUuid: string = '';
  readOnly: boolean = false;

  // Questions that open expanded: the blank one shown when there are none yet, and ones added with "Add new question".
  private expandedQuestions = new Set<FormGroup>();
  // The question added last with "Add new question"; its title opens for editing.
  private addedQuestion?: FormGroup;
  // Fired when a question is deleted, to stop its autosave.
  private questionDeleted = new Map<FormGroup, Subject<void>>();
  // Questions whose delete request is in flight; their delete button stays disabled until it returns.
  private deletingQuestions = new Set<FormGroup>();

  @ViewChildren(KnowledgeCheckQuestionPanelComponent) private panels?: QueryList<KnowledgeCheckQuestionPanelComponent>;
  // The collapsed question an answer point is being dragged over, and the timer that expands it.
  private hoveredPanel?: KnowledgeCheckQuestionPanelComponent;
  private expandTimer?: ReturnType<typeof setTimeout>;

  constructor(
    private route: ActivatedRoute,
    private authorService: AuthorService,
    private authorReactiveFormService: AuthorReactiveFormService,
    private dragDropRegistry: DragDropRegistry,
    private ngZone: NgZone,
    private injector: Injector,
  ) {}

  ngOnInit(): void {
    this.callUuid = this.route.snapshot.paramMap?.get('uuid')!;
    this.readOnly = this.authorReactiveFormService.isReadOnly(this.callUuid);
    this.watchAnswerPointDrag();

    this.authorReactiveFormService
      .getForm(this.callUuid)
      .pipe(take(1))
      .subscribe((form) => {
        const questions = form.get(SimulationConstants.QUESTIONS_SECTION) as FormArray<FormGroup>;
        this.loadQuestions(questions);
      });
  }

  addQuestion(): void {
    const question = this.authorReactiveFormService.buildQuestionGroup();
    this.expandedQuestions.add(question);
    this.addedQuestion = question;
    this.addQuestionGroup(question);
  }

  opensExpanded(question: FormGroup): boolean {
    return this.expandedQuestions.has(question);
  }

  isAddedQuestion(question: FormGroup): boolean {
    return question === this.addedQuestion;
  }

  // Only a question created on the server can be dragged; it has no uuid to reorder by before that.
  isSaved(question: FormGroup): boolean {
    return !!question.get('questionUuid')?.value;
  }

  asFormGroup(item: DraggableItem): FormGroup {
    return item as unknown as FormGroup;
  }

  // Unsaved questions stay at the end (a question can be dropped above them, not below), so the
  // order on screen matches the server once they are created there. Only the saved questions whose
  // sequence changed are sent, with their new sequence.
  onQuestionsReordered(items: DraggableItem[]): void {
    if (!this.questions) return;
    const reordered = items as unknown as FormGroup[];
    const saved = reordered.filter((question) => this.isSaved(question));
    const unsaved = reordered.filter((question) => !this.isSaved(question));

    this.questions.clear({ emitEvent: false });
    [...saved, ...unsaved].forEach((question) => this.questions?.push(question, { emitEvent: false }));
    this.syncQuestionItems();

    if (this.authorReactiveFormService.isReadOnly(this.callUuid)) return;

    const moved = saved
      .map((question, index) => ({ question, sequence: index + 1 }))
      .filter(({ question, sequence }) => question.get('sequence')?.value !== sequence);
    if (!moved.length) return; 

    this.authorReactiveFormService.updateFormUpdatedAt(this.callUuid);
    this.authorService
      .updateGuidedQuestionsWithAnswers(this.callUuid, {
        questions: moved.map(({ question, sequence }) => this.toGuidedQuestionUpdate(question.getRawValue(), sequence)),
      })
      .subscribe({
        next: () =>
          moved.forEach(({ question, sequence }) => question.patchValue({ sequence }, { emitEvent: false })),
        error: (err) => console.error('Error saving Knowledge Check question order:', err),
      });
  }

  // Only saved questions take part in moving answer points; an unsaved question has no uuid to move them to.
  answerPointListId(question: FormGroup): string | undefined {
    const questionUuid = question.get('questionUuid')?.value;
    return questionUuid ? ANSWER_POINT_LIST_ID_PREFIX + questionUuid : undefined;
  }

  connectedAnswerPointListIds(question: FormGroup): string[] {
    if (!this.answerPointListId(question)) return [];
    return (this.questions?.controls ?? [])
      .filter((other) => other !== question)
      .map((other) => this.answerPointListId(other))
      .filter((listId): listId is string => !!listId);
  }

  // The drop has already put `question`'s answer points in their new order. One moved in from another
  // question is removed there (the drop only updates the target list). Like the facts reorder, only the
  // answer points from the first changed position onwards are sent, with their new sequence.
  onAnswerPointsReordered(question: FormGroup, previous: FormGroup[]): void {
    const answerPoints = this.answerPointsOf(question);
    answerPoints.controls
      .filter((answerPoint) => !previous.includes(answerPoint))
      .forEach((answerPoint) => this.removeFromSourceQuestion(question, answerPoint));

    const firstChangedIndex = answerPoints.controls.findIndex((answerPoint, index) => answerPoint !== previous[index]);
    if (firstChangedIndex === -1) return; // dropped back where it was
    if (this.authorReactiveFormService.isReadOnly(this.callUuid)) return;

    const impacted = answerPoints.controls.slice(firstChangedIndex);
    impacted.forEach((answerPoint, offset) =>
      answerPoint.patchValue({ sequence: firstChangedIndex + offset + 1 }, { emitEvent: false }),
    );

    const value = question.getRawValue();
    this.authorReactiveFormService.updateFormUpdatedAt(this.callUuid);
    this.authorService
      .updateGuidedQuestionsWithAnswers(this.callUuid, {
        questions: [
          {
            ...this.toGuidedQuestionUpdate(value),
            answers: impacted.map((answerPoint) => this.toGuidedAnswerPoint(answerPoint.getRawValue(), value.questionUuid)),
          },
        ],
      })
      .subscribe({
        error: (err) => console.error('Error saving Knowledge Check answer point order:', err),
      });
  }

  deleteQuestion(question: FormGroup): void {
    if (this.authorReactiveFormService.isReadOnly(this.callUuid)) return;

    const questionUuid = question.get('questionUuid')?.value;
    if (!questionUuid) {
      this.removeQuestion(question);
      return;
    }

    if (this.deletingQuestions.has(question)) return;
    this.deletingQuestions.add(question);

    this.authorService.deleteKnowledgeCheckQuestion(this.callUuid, questionUuid).subscribe({
      next: () => {
        this.deletingQuestions.delete(question);
        this.removeQuestion(question);
        this.authorReactiveFormService.updateFormUpdatedAt(this.callUuid);
      },
      error: (err) => {
        this.deletingQuestions.delete(question);
        console.error('Error deleting Knowledge Check question:', err);
      },
    });
  }

  isDeleting(question: FormGroup): boolean {
    return this.deletingQuestions.has(question);
  }

  private loadQuestions(questions: FormArray<FormGroup>): void {
    this.authorService.getGuidedQuestionsWithAnswers(this.callUuid).subscribe({
      next: (response) => {
        this.questions = questions;
        this.questions.clear();
        [...(response.questions ?? [])]
          .sort((a, b) => a.questionSequence - b.questionSequence)
          .forEach((question) =>
            this.addQuestionGroup(this.authorReactiveFormService.buildQuestionGroup(this.mapGuidedQuestion(question))),
          );

        if (!this.questions.length) {
          const defaultQuestion = this.authorReactiveFormService.buildQuestionGroup();
          this.expandedQuestions.add(defaultQuestion);
          this.addQuestionGroup(defaultQuestion);
        }

        this.questions.valueChanges
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe(() => this.recomputeHasBlankQuestion());
      },
      error: (err) => {
        console.error('Error loading guided questions with answers:', err);
        this.questions = questions;
        this.syncQuestionItems();
      },
    });
  }

  private mapGuidedQuestion(q: IGuidedQuestion): Partial<IKnowledgeCheckQuestionForm> {
    return {
      questionUuid: q.questionUuid,
      callGuidedUuid: q.callGuidedUuid,
      title: q.questionName,
      script: q.questionScript,
      hint: q.hints,
      resourceNote: q.reference,
      sequence: q.questionSequence,
      answerPoints: q.answers
        .slice()
        .sort((a, b) => a.answerSequence - b.answerSequence)
        .map((ap) => ({
          uuid: ap.uuid,
          verb: ap.verb.toLowerCase() as KnowledgeCheckAnswerPointVerb,
          displayText: ap.displayFactCorrect ?? '',
          displayFactIncorrect: ap.displayFactIncorrect,
          sequence: ap.answerSequence,
        })),
    };
  }

  private answerPointsOf(question: FormGroup): FormArray<FormGroup> {
    return question.get('answerPoints') as FormArray<FormGroup>;
  }

  private removeFromSourceQuestion(target: FormGroup, answerPoint: FormGroup): void {
    const source = this.questions?.controls.find(
      (question) => question !== target && this.answerPointsOf(question).controls.includes(answerPoint),
    );
    if (!source) return;
    const sourceAnswerPoints = this.answerPointsOf(source);
    sourceAnswerPoints.removeAt(sourceAnswerPoints.controls.indexOf(answerPoint), { emitEvent: false });
  }

  // Expands a collapsed question once an answer point is held over it, so it can be dropped at any position.
  private watchAnswerPointDrag(): void {
    this.dragDropRegistry.pointerMove
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((event) => this.handleDragMove(event));
    this.dragDropRegistry.pointerUp
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.setHoveredPanel(undefined));
    this.destroyRef.onDestroy(() => clearTimeout(this.expandTimer));
  }

  private handleDragMove(event: MouseEvent | TouchEvent): void {
    const point = 'touches' in event ? (event.touches[0] ?? event.changedTouches[0]) : event;
    if (!point) return;

    const element = document.elementFromPoint(point.clientX, point.clientY)?.closest('knowledge-check-question-panel');
    const panel = element ? this.panels?.find((p) => p.elementRef.nativeElement === element) : undefined;
    this.setHoveredPanel(panel && !panel.isExpanded() && this.isDraggingAnswerPoint() ? panel : undefined);
  }

  private setHoveredPanel(panel: KnowledgeCheckQuestionPanelComponent | undefined): void {
    if (panel === this.hoveredPanel) return;
    clearTimeout(this.expandTimer);
    this.hoveredPanel = panel;
    if (!panel) return;

    this.expandTimer = setTimeout(
      () => this.ngZone.run(() => this.expandWhileDragging(panel)),
      EXPAND_ON_DRAG_HOVER_DELAY,
    );
  }

  private expandWhileDragging(panel: KnowledgeCheckQuestionPanelComponent): void {
    this.hoveredPanel = undefined;
    panel.expand();
    // CDK measures the connected lists when the drag starts, so the newly shown list (and the questions
    // pushed down by it) would keep stale positions. A scroll event makes CDK re-measure them mid-drag.
    afterNextRender(() => document.dispatchEvent(new Event('scroll')), { injector: this.injector });
  }

  // The drag preview is a copy of the dragged row. A dragged question's preview holds its answer point rows too.
  private isDraggingAnswerPoint(): boolean {
    return (
      !!document.querySelector('.cdk-drag-preview knowledge-check-answer-point-row') &&
      !document.querySelector('.cdk-drag-preview knowledge-check-question-panel')
    );
  }

  private addQuestionGroup(question: FormGroup): void {
    this.questions?.push(question);
    this.syncQuestionItems();
    this.setupAutoSave(question);
    this.recomputeHasBlankQuestion();
  }

  private syncQuestionItems(): void {
    this.questionItems = (this.questions?.controls.slice() ?? []) as unknown as DraggableItem[];
  }

  private removeQuestion(question: FormGroup): void {
    this.questionDeleted.get(question)?.next();
    this.questionDeleted.delete(question);

    const index = this.questions?.controls.indexOf(question) ?? -1;
    if (index !== -1) this.questions?.removeAt(index);
    this.syncQuestionItems();

    this.expandedQuestions.delete(question);
    if (this.addedQuestion === question) this.addedQuestion = undefined;
    this.recomputeHasBlankQuestion();
  }

  private recomputeHasBlankQuestion(): void {
    this.hasBlankQuestion.set(
      (this.questions?.controls ?? []).some(
        (question) => !question.get('questionUuid')?.value && !this.hasContent(question),
      ),
    );
  }

  private hasContent(question: FormGroup): boolean {
    return QUESTION_FIELDS.some((field) => (question.get(field)?.value ?? '').toString().trim());
  }

  private setupAutoSave(question: FormGroup): void {
    let lastSaved = question.getRawValue();
    const deleted$ = new Subject<void>();
    this.questionDeleted.set(question, deleted$);

    question.valueChanges
      .pipe(
        debounceTime(SimulationConstants.AUTOSAVE_GENERIC_DEBOUNCE),
        takeUntil(deleted$),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        if (this.authorReactiveFormService.isReadOnly(this.callUuid)) return;

        const value = question.getRawValue();
        if (!QUESTION_FIELDS.some((field) => value[field] !== lastSaved[field])) return;

        const questionUuid = value.questionUuid;
        if (!questionUuid && !this.hasContent(question)) return;

        lastSaved = value;
        this.authorReactiveFormService.updateFormUpdatedAt(this.callUuid);

        if (questionUuid) {
          this.authorService
            .updateGuidedQuestionsWithAnswers(this.callUuid, { questions: [this.toGuidedQuestionUpdate(value)] })
            .subscribe({
              error: (err) => console.error('Error saving Knowledge Check question:', err),
            });
          return;
        }

        this.authorService
          .createKnowledgeCheckQuestion(this.callUuid, {
            title: value.title,
            script: value.script,
            hint: value.hint,
            resourceNote: value.resourceNote,
          })
          .subscribe({
            next: (saved) => {
              if (!saved?.questionUuid) return;
              question.patchValue(
                { callGuidedUuid: saved.callGuidedUuid, questionUuid: saved.questionUuid, sequence: saved.sequence },
                { emitEvent: false },
              );
              this.recomputeHasBlankQuestion();
            },
            error: (err) => console.error('Error saving Knowledge Check question:', err),
          });
      });
  }

  // Answers are sent empty: the update API ignores them for now (answer points save separately).
  private toGuidedQuestionUpdate(
    value: IKnowledgeCheckQuestionForm,
    questionSequence: number | null = value.sequence ?? null,
  ): IGuidedQuestionUpdate {
    return {
      callGuidedUuid: value.callGuidedUuid,
      questionUuid: value.questionUuid,
      questionName: value.title ?? '',
      questionScript: value.script,
      questionSequence,
      hints: value.hint,
      reference: value.resourceNote ?? '',
      answers: [],
    };
  }

  // Same shape as createGuidedAnswer sends.
  private toGuidedAnswerPoint(answerPoint: IKnowledgeCheckAnswerPoint, questionUuid: string): IGuidedAnswerPointUpdate {
    return {
      uuid: answerPoint.uuid ?? '',
      questionUuid,
      answerSequence: answerPoint.sequence ?? 0,
      verb: answerPoint.verb.toUpperCase(),
      displayFactCorrect: answerPoint.displayText,
      displayFactIncorrect: answerPoint.displayFactIncorrect ?? null,
      factText: null,
    };
  }
}
